import type { Translate } from '../flows/builder/components/nodes';
import type { FlowDefinition, JsonObject } from '../flows/builder/mapper';

/** The trigger field that marks the flow the Knowledge base page manages. The mapper keeps trigger fields verbatim. */
export const ASSISTANT_ROLE = 'knowledge_assistant';

export type EscalationTargets = { email?: string; whatsapp?: string; webhook?: string };

/**
 * The flow behind "Answer questions automatically": answer any message from
 * the whole knowledge base; when the AI is not sure, say so and ASK whether
 * they would like someone from the team. "No" closes politely and nobody is
 * told. "Yes" asks what they need and how to reach them, saves it on the
 * contact, tells the team, and closes. Built as an ordinary flow so the
 * builder can change every word.
 *
 * The question is the owner's ruling of 14 September 2026: the bot must never
 * pass a conversation to people without asking first.
 */
export function knowledgeAssistantDefinition(t: Translate, targets: EscalationTargets): FlowDefinition {
  const channels: JsonObject[] = [];
  if (targets.email?.trim()) channels.push({ type: 'email', to: targets.email.trim() });
  if (targets.whatsapp?.trim()) channels.push({ type: 'whatsapp', to: targets.whatsapp.trim() });
  if (targets.webhook?.trim()) channels.push({ type: 'webhook', url: targets.webhook.trim() });
  return {
    version: 2,
    trigger: { type: 'default', role: ASSISTANT_ROLE },
    nodes: [
      { id: 'answer', type: 'llm', name: t('knowledge.assistant.answerName'), mode: 'reply', kb_scope: 'all', send_reply: true, unsure_below: 0.6, system_prompt: t('knowledge.assistant.prompt'), save_as: 'llm' },
      { id: 'not_sure', type: 'send', body: t('knowledge.assistant.unsureMessage') },
      { id: 'offer', type: 'ask', name: t('knowledge.assistant.offerName'), question: t('knowledge.assistant.offer'), variable: 'wants_help', validate: 'option', options: [{ id: 'yes', title: t('knowledge.assistant.offerYes') }, { id: 'no', title: t('knowledge.assistant.offerNo') }], retry_message: t('knowledge.assistant.offerRetry') },
      { id: 'declined', type: 'end', message: t('knowledge.assistant.declined') },
      { id: 'ask_need', type: 'ask', question: t('knowledge.assistant.askNeed'), variable: 'need', validate: 'not_empty' },
      { id: 'ask_name', type: 'ask', question: t('knowledge.assistant.askName'), variable: 'name', validate: 'not_empty' },
      { id: 'ask_contact', type: 'ask', question: t('knowledge.assistant.askContact'), variable: 'contact_detail', validate: 'not_empty' },
      { id: 'save', type: 'contact', contact_name: '{{name}}', fields: { need: '{{need}}', contact_detail: '{{contact_detail}}' }, add_tags: ['needs-human'] },
      { id: 'notify', type: 'notify', name: t('knowledge.assistant.notifyName'), channels, subject: t('knowledge.assistant.subject'), include_details: true, include_transcript: true },
      { id: 'done', type: 'end', message: t('knowledge.assistant.done') }
    ],
    edges: [
      { from: 'answer', to: 'not_sure', branch: 'unsure' },
      { from: 'not_sure', to: 'offer' },
      { from: 'offer', to: 'ask_need', branch: 'yes' },
      { from: 'offer', to: 'declined', branch: 'no' },
      { from: 'ask_need', to: 'ask_name' },
      { from: 'ask_name', to: 'ask_contact' },
      { from: 'ask_contact', to: 'save' },
      { from: 'save', to: 'notify' },
      { from: 'notify', to: 'done' }
    ]
  };
}

/** Is this flow the one the Knowledge base page manages? */
export function isKnowledgeAssistant(definition: unknown): boolean {
  if (!definition || typeof definition !== 'object') return false;
  const trigger = (definition as JsonObject).trigger;
  return !!trigger && typeof trigger === 'object' && (trigger as JsonObject).role === ASSISTANT_ROLE;
}

/** Where a flow's notify steps reach the team, for the summary on the page. */
export function notifyTargets(definition: unknown): string[] {
  if (!definition || typeof definition !== 'object') return [];
  const nodes = (definition as JsonObject).nodes;
  if (!Array.isArray(nodes)) return [];
  const out: string[] = [];
  for (const n of nodes) {
    if (!n || typeof n !== 'object' || (n as JsonObject).type !== 'notify') continue;
    const channels = (n as JsonObject).channels;
    if (!Array.isArray(channels)) continue;
    for (const c of channels) {
      if (!c || typeof c !== 'object') continue;
      const target = String((c as JsonObject).to ?? (c as JsonObject).url ?? '').trim();
      if (target) out.push(`${String((c as JsonObject).type ?? 'email')}: ${target}`);
    }
  }
  return out;
}

/** Does another active flow answer every message too? The most recently saved one wins in the engine. */
export function isCatchAll(definition: unknown): boolean {
  if (!definition || typeof definition !== 'object') return false;
  const trigger = (definition as JsonObject).trigger;
  if (!trigger || typeof trigger !== 'object') return false;
  const tr = trigger as JsonObject;
  if (tr.type === 'always' || tr.type === 'default') return true;
  const kws = Array.isArray(tr.keywords) ? tr.keywords : tr.keyword ? [tr.keyword] : [];
  return (tr.type === 'message' || tr.type === 'keyword') && kws.length === 0;
}
